// src/components/BulkMessageManager/BulkMessageManager.jsx
/**
 * Gerenciador Visual de Envio em Massa
 * Exibe o progresso dos envios em massa com todas as funcionalidades
 */

import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiLoader,
  FiAlertTriangle,
  FiMail,
  FiTrash2,
  FiStopCircle
} from "react-icons/fi";
import useBulkMessageStore, { getRandomFriendlyMessage } from "../../services/useBulkMessageStore";
import "./BulkMessageManager.css";

const ProcessCard = ({ process, onHide, onCancel, onFetchErrors }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [friendlyMessage, setFriendlyMessage] = useState(() => getRandomFriendlyMessage());
  const [cancelling, setCancelling] = useState(false);

  // Rotaciona mensagem amigável a cada 8 segundos durante processamento
  useEffect(() => {
    if (process.status !== "PROCESSING") return;

    const interval = setInterval(() => {
      setFriendlyMessage(getRandomFriendlyMessage());
    }, 8000);

    return () => clearInterval(interval);
  }, [process.status]);

  // Carrega erros quando expandir
  useEffect(() => {
    if (isExpanded && process.failed_count > 0 && errors.length === 0) {
      loadErrors();
    }
  }, [isExpanded, process.failed_count]);

  const loadErrors = async () => {
    setLoadingErrors(true);
    const fetchedErrors = await onFetchErrors(process.id);
    setErrors(fetchedErrors);
    setLoadingErrors(false);
  };

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    await onCancel(process.id);
    setCancelling(false);
  };

  const handleHide = async () => {
    await onHide(process.id);
  };

  const getStatusInfo = () => {
    switch (process.status) {
      case "PENDING":
        return { icon: <FiLoader className="bulk-icon-spin" />, text: "Preparando...", className: "pending" };
      case "PROCESSING":
        return { icon: <FiLoader className="bulk-icon-spin" />, text: "Enviando...", className: "processing" };
      case "COMPLETED":
        return { icon: <FiCheck />, text: "Finalizado", className: "completed" };
      case "COMPLETED_WITH_ERRORS":
        return { icon: <FiAlertTriangle />, text: "Finalizado com erros", className: "warning" };
      case "FAILED":
        return { icon: <FiX />, text: "Falhou", className: "failed" };
      case "CANCELLED":
        return { icon: <FiStopCircle />, text: "Cancelado", className: "cancelled" };
      default:
        return { icon: <FiLoader />, text: process.status, className: "pending" };
    }
  };

  const statusInfo = getStatusInfo();
  const progressPercent = process.progress_percent || 0;
  const sentCount = process.sent_count || 0;
  const failedCount = process.failed_count || 0;
  const totalCount = process.total_messages || 0;
  const isActive = process.status === "PENDING" || process.status === "PROCESSING";
  const isFinished = ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED", "CANCELLED"].includes(process.status);
  const isWhatsApp = process.channel === "WHATSAPP";
  const hasErrors = failedCount > 0;

  return (
    <div className={`bulk-process-card ${statusInfo.className}`}>
      <div className="bulk-process-header">
        <div className="bulk-process-channel">
          {isWhatsApp ? (
            <FaWhatsapp className="bulk-channel-icon whatsapp" />
          ) : (
            <FiMail className="bulk-channel-icon email" />
          )}
          <span className="bulk-process-title">
            {isWhatsApp ? "WhatsApp" : "Email"} em massa
          </span>
        </div>
        <div className={`bulk-process-status ${statusInfo.className}`}>
          {statusInfo.icon}
          <span>{statusInfo.text}</span>
        </div>
      </div>

      {/* Barra de progresso */}
      {isActive && (
        <div className="bulk-progress-container">
          <div className="bulk-progress-bar">
            <div
              className="bulk-progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="bulk-progress-text">
            {sentCount + failedCount} de {totalCount}
          </span>
        </div>
      )}

      {/* Estatísticas */}
      <div className="bulk-process-stats">
        <span className="bulk-stat success">
          <FiCheck /> {sentCount} enviadas
        </span>
        {failedCount > 0 && (
          <span className="bulk-stat error">
            <FiAlertTriangle /> {failedCount} erros
          </span>
        )}
        {isActive && (
          <span className="bulk-stat pending">
            {process.pending_count || (totalCount - sentCount - failedCount)} pendentes
          </span>
        )}
      </div>

      {/* Mensagem amigável durante processamento */}
      {process.status === "PROCESSING" && (
        <div className="bulk-friendly-message">
          {friendlyMessage}
        </div>
      )}

      {/* Duração para processos finalizados */}
      {isFinished && process.duration_formatted && (
        <div className="bulk-duration">
          Tempo: {process.duration_formatted}
        </div>
      )}

      {/* Ações */}
      <div className="bulk-process-actions">
        {isActive && (
          <button
            className="bulk-action-btn cancel"
            onClick={handleCancel}
            disabled={cancelling}
          >
            <FiStopCircle />
            {cancelling ? "Cancelando..." : "Cancelar"}
          </button>
        )}

        {hasErrors && (
          <button
            className="bulk-action-btn details"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>Ocultar erros <FiChevronUp /></>
            ) : (
              <>Ver erros ({failedCount}) <FiChevronDown /></>
            )}
          </button>
        )}

        {isFinished && (
          <button className="bulk-close-btn" onClick={handleHide}>
            <FiX /> Fechar
          </button>
        )}
      </div>

      {/* Lista de erros expandida */}
      {isExpanded && (
        <div className="bulk-errors-section">
          {loadingErrors ? (
            <div className="bulk-errors-loading">
              <FiLoader className="bulk-icon-spin" /> Carregando erros...
            </div>
          ) : errors.length === 0 ? (
            <div className="bulk-errors-empty">Nenhum erro encontrado.</div>
          ) : (
            <div className="bulk-errors-list">
              {errors.map((error, index) => (
                <div key={error.id || index} className="bulk-error-item">
                  <div className="bulk-error-header">
                    <span className="bulk-error-name">
                      {error.lead_name || "Sem nome"}
                    </span>
                    <span className="bulk-error-phone">{error.phone}</span>
                  </div>
                  <div className="bulk-error-message">{error.error_message}</div>
                  {error.failed_at && (
                    <div className="bulk-error-time">
                      {new Date(error.failed_at).toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BulkMessageManager = () => {
  const processes = useBulkMessageStore((state) => state.processes);
  const hideProcess = useBulkMessageStore((state) => state.hideProcess);
  const cancelProcess = useBulkMessageStore((state) => state.cancelProcess);
  const fetchProcessErrors = useBulkMessageStore((state) => state.fetchProcessErrors);
  const fetchProcesses = useBulkMessageStore((state) => state.fetchProcesses);
  const startPolling = useBulkMessageStore((state) => state.startPolling);
  const stopPolling = useBulkMessageStore((state) => state.stopPolling);
  const clearCompletedProcesses = useBulkMessageStore((state) => state.clearCompletedProcesses);

  const [isExpanded, setIsExpanded] = useState(true);

  // Carrega processos do backend ao montar o componente
  useEffect(() => {
    fetchProcesses();
  }, []);

  // Inicia polling quando há processos ativos
  useEffect(() => {
    const hasActive = processes.some(p => p.status === "PENDING" || p.status === "PROCESSING");
    if (hasActive) {
      startPolling();
    }
    return () => stopPolling();
  }, [processes.length, startPolling, stopPolling]);

  if (processes.length === 0) {
    return null;
  }

  const activeCount = processes.filter(p => p.status === "PENDING" || p.status === "PROCESSING").length;
  const completedCount = processes.length - activeCount;

  return (
    <div className="bulk-manager-container">
      <div
        className="bulk-manager-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="bulk-manager-title">
          <FaWhatsapp className="bulk-manager-icon" />
          <span>Envios em Massa</span>
          {activeCount > 0 && (
            <span className="bulk-badge active">{activeCount} ativo{activeCount !== 1 ? "s" : ""}</span>
          )}
          {completedCount > 0 && activeCount === 0 && (
            <span className="bulk-badge completed">{completedCount} finalizado{completedCount !== 1 ? "s" : ""}</span>
          )}
        </div>
        <div className="bulk-manager-actions">
          {completedCount > 0 && (
            <button
              className="bulk-header-btn"
              onClick={(e) => {
                e.stopPropagation();
                clearCompletedProcesses();
              }}
              title="Limpar finalizados"
            >
              <FiTrash2 />
            </button>
          )}
          <button className="bulk-header-btn">
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="bulk-manager-content">
          {processes.map((process) => (
            <ProcessCard
              key={process.id}
              process={process}
              onHide={hideProcess}
              onCancel={cancelProcess}
              onFetchErrors={fetchProcessErrors}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkMessageManager;
